"use client";

import { useEffect } from "react";

import { trackEvent } from "@/utils/analytics";

// ----------------------------------------------------------------------------
// Forwards Core Web Vitals (LCP / CLS / INP / FCP / TTFB) to GA4 as
// `web_vital` events so we can correlate page-speed with conversion rate
// in GA4 Explore — the single biggest lever for both SEO ranking and UX
// prioritization.
//
// Uses the `web-vitals` library (1.5KB gzipped). The library handles all
// the browser-quirk edge cases (interaction-attributed INP, layout-shift
// score aggregation, soft-nav LCP) so we don't have to.
//
// trackEvent already filters bots + respects the analytics consent cookie,
// so we don't need any extra guards here.
// ----------------------------------------------------------------------------

const WebVitalsReporter = () => {
  useEffect(() => {
    // Dynamic import so the web-vitals JS isn't bundled into the initial
    // page load — it ships only after the first interactive frame.
    import("web-vitals").then(({ onLCP, onCLS, onINP, onFCP, onTTFB }) => {
      const report = (metric: {
        name: string;
        value: number;
        rating: "good" | "needs-improvement" | "poor";
        id: string;
      }) => {
        trackEvent("web_vital", {
          metric_name: metric.name,
          // CLS is a unitless score; everything else is in milliseconds.
          // Round to integer so GA4 sums don't have float drift.
          metric_value: Math.round(
            metric.name === "CLS" ? metric.value * 1000 : metric.value,
          ),
          metric_rating: metric.rating,
          // GA4 dedupe key — distinct id per Vitals report so multiple
          // observations of the same page aren't double-counted.
          metric_id: metric.id,
        });
      };

      onLCP(report);
      onCLS(report);
      onINP(report);
      onFCP(report);
      onTTFB(report);
    });
  }, []);

  return null;
};

export default WebVitalsReporter;
